import { elementOutlinePolygon, MORPH_SHAPES } from "@excalidraw/element";

import { definePreset, kf, rad, setStatic, timing } from "./helpers";

import type { ElementAnimation } from "../types";

import type { AnimationPreset, ParamSchema } from "./types";

/** Panel renders params keyed "shape" as a target-shape dropdown. */
const SHAPE_SCHEMA: ParamSchema = {
  key: "shape",
  label: "Shape",
  min: 0,
  max: 5,
  step: 1,
};

const svgOutlineCache = new Map<string, number[] | null>();

/**
 * Samples raw SVG path data into a normalized (0..1 bbox) flattened outline
 * usable as a morph target — the browser's own path geometry does the parsing
 * (arcs, beziers, relative commands all included), no parser dependency.
 * Returns null when the path is invalid or the environment (e.g. jsdom) has
 * no path geometry — callers fall back to the built-in `shape` param.
 */
export const svgPathToOutline = (d: string, samples = 64): number[] | null => {
  const key = d.trim();
  if (!key) {
    return null;
  }
  const cached = svgOutlineCache.get(key);
  if (cached !== undefined) {
    return cached;
  }
  let outline: number[] | null = null;
  try {
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute("d", key);
    const total = path.getTotalLength();
    if (Number.isFinite(total) && total > 0) {
      const pts: [number, number][] = [];
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      for (let i = 0; i < samples; i++) {
        const p = path.getPointAtLength((i / samples) * total);
        pts.push([p.x, p.y]);
        minX = Math.min(minX, p.x);
        minY = Math.min(minY, p.y);
        maxX = Math.max(maxX, p.x);
        maxY = Math.max(maxY, p.y);
      }
      const w = maxX - minX || 1;
      const h = maxY - minY || 1;
      outline = pts.flatMap(([x, y]) => [(x - minX) / w, (y - minY) / h]);
    }
  } catch {
    outline = null;
  }
  svgOutlineCache.set(key, outline);
  return outline;
};

/** Static morph channels for a clip: a custom SVG path (when set and valid)
 * wins over the built-in `shape` param. */
export const morphStatics = (cfg: ElementAnimation) => {
  const custom = cfg.customPath ? svgPathToOutline(cfg.customPath) : null;
  return {
    morphShape: cfg.params.shape ?? MORPH_SHAPES.ellipse,
    ...(custom ? { morphOutline: custom } : {}),
  };
};

const INTENSITY_SCHEMA = {
  key: "intensity",
  label: "Intensity",
  min: 0.05,
  max: 0.9,
  step: 0.05,
};

export const morphPresets: AnimationPreset[] = [
  definePreset(
    "shapeMorph",
    "Morph To",
    "morph",
    {
      params: { shape: MORPH_SHAPES.ellipse },
      schema: [SHAPE_SCHEMA],
      easing: "power2.inOut",
    },
    (tl, target, _element, cfg) => {
      // true outline morph into the target shape (rendered as an
      // interpolated path); works on rectangles, ellipses, diamonds and
      // path elements (lines/arrows/pencil strokes morph from their own
      // drawn outline)
      setStatic(tl, target, morphStatics(cfg));
      tl.fromTo(
        target,
        { morphProgress: 0 },
        { morphProgress: 1, ...timing(cfg) },
        0,
      );
    },
  ),
  definePreset(
    "shapeMorphCycle",
    "Morph Cycle",
    "morph",
    {
      params: { shape: MORPH_SHAPES.ellipse },
      schema: [SHAPE_SCHEMA],
      duration: 2000,
    },
    (tl, target, _element, cfg) => {
      setStatic(tl, target, morphStatics(cfg));
      tl.to(target, kf(cfg, { morphProgress: [0, 1, 1, 0] }, "sine.inOut"), 0);
    },
  ),
  definePreset(
    "flipX",
    "Mirror X",
    "morph",
    {},
    (tl, target, _element, cfg) => {
      tl.to(target, kf(cfg, { scaleX: [1, -1, 1] }, "sine.inOut"), 0);
    },
  ),
  definePreset(
    "flipY",
    "Mirror Y",
    "morph",
    {},
    (tl, target, _element, cfg) => {
      tl.to(target, kf(cfg, { scaleY: [1, -1, 1] }, "sine.inOut"), 0);
    },
  ),
  definePreset(
    "squash",
    "Squash",
    "morph",
    { params: { intensity: 0.25 }, schema: [INTENSITY_SCHEMA] },
    (tl, target, _element, cfg) => {
      const i = cfg.params.intensity ?? 0.25;
      tl.to(
        target,
        kf(cfg, { scaleX: [1, 1 + i, 1], scaleY: [1, 1 - i, 1] }, "sine.inOut"),
        0,
      );
    },
  ),
  definePreset(
    "stretch",
    "Stretch",
    "morph",
    { params: { intensity: 0.25 }, schema: [INTENSITY_SCHEMA] },
    (tl, target, _element, cfg) => {
      const i = cfg.params.intensity ?? 0.25;
      tl.to(
        target,
        kf(cfg, { scaleX: [1, 1 - i, 1], scaleY: [1, 1 + i, 1] }, "sine.inOut"),
        0,
      );
    },
  ),
  definePreset(
    "skewMorph",
    "Skew",
    "morph",
    {
      params: { degrees: 20 },
      schema: [{ key: "degrees", label: "Degrees", min: 1, max: 60, step: 1 }],
    },
    (tl, target, _element, cfg) => {
      const d = cfg.params.degrees ?? 20;
      tl.to(
        target,
        kf(cfg, { skewX: [0, rad(d), rad(-d), 0] }, "sine.inOut"),
        0,
      );
    },
  ),
  definePreset(
    "twist",
    "Twist",
    "morph",
    {
      params: { degrees: 30 },
      schema: [{ key: "degrees", label: "Degrees", min: 1, max: 180, step: 5 }],
    },
    (tl, target, _element, cfg) => {
      const d = cfg.params.degrees ?? 30;
      tl.to(
        target,
        kf(
          cfg,
          {
            angleDelta: [0, rad(d), 0],
            skewX: [0, rad(d / 3), 0],
            scaleY: [1, 0.9, 1],
          },
          "sine.inOut",
        ),
        0,
      );
    },
  ),
  definePreset(
    "morphToElement",
    "Morph To Element",
    "morph",
    {
      params: { travel: 0 },
      schema: [
        { key: "travel", label: "Move to target", min: 0, max: 1, step: 1 },
      ],
      easing: "power2.inOut",
      duration: 1500,
    },
    (tl, target, element, cfg, sceneElements) => {
      // anime.js morphTo: outline-morphs into the referenced scene element
      // (pathElementId — the topmost selected element when applied from the
      // panel); travel=1 additionally moves/scales onto the target's bbox
      // (PowerPoint-morph). Without a target: built-in ellipse morph.
      const morphTarget = sceneElements?.find(
        (el) =>
          el.id === cfg.pathElementId && !el.isDeleted && el.id !== element.id,
      );
      // precedence: referenced element > custom SVG path > built-in ellipse
      const customOutline = cfg.customPath
        ? svgPathToOutline(cfg.customPath)
        : null;
      setStatic(tl, target, {
        morphShape: MORPH_SHAPES.ellipse,
        ...(morphTarget
          ? { morphOutline: elementOutlinePolygon(morphTarget).flat() }
          : customOutline
          ? { morphOutline: customOutline }
          : {}),
      });
      tl.fromTo(
        target,
        { morphProgress: 0 },
        { morphProgress: 1, ...timing(cfg) },
        0,
      );
      if ((cfg.params.travel ?? 0) >= 1 && morphTarget) {
        tl.to(
          target,
          {
            dx:
              morphTarget.x +
              morphTarget.width / 2 -
              (element.x + element.width / 2),
            dy:
              morphTarget.y +
              morphTarget.height / 2 -
              (element.y + element.height / 2),
            scaleX: morphTarget.width / (element.width || 1),
            scaleY: morphTarget.height / (element.height || 1),
            ...timing(cfg),
          },
          0,
        );
      }
    },
  ),
];
